import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Chip,
  Card,
  CardContent,
  Stack,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Divider,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Tab,
  DialogTitle,
   Breadcrumbs,
  Link as MUILink,
  Grid
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation, Link } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import { useDispatch, useSelector } from "react-redux";
import TablePagination from "@mui/material/TablePagination";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  deleteBankStatement,
  getBuyerAllInvoices,
  getInvoiceStatement,
  updateCredit,
} from "../redux/slices/invoiceSlice";
import GetQRCodeModal from "../Components/GetQRCodeModal";
import AddEntryModal from "../Components/AddEntryModal";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import PageTitle from "../Components/PageTitle";

// Update status colors to handle "Overdue"
const statusColor = (status) => {
  switch (status) {
    case "Paid":
    case "Completed":
      return "success";
    case "Pending":
      return "warning";
    case "Overdue":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (amount) => {
  return amount?.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function Ledger() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.user);
  const id = user?._id;
  const [openBankStatement, setOpenBankStatement] = useState(false);
  const [selectedInvoiceForStatement, setSelectedInvoiceForStatement] =
    useState(null);
  const {
    buyerInvoices = [],
    loading,
    buyerPagination,
  } = useSelector((state) => state.invoice);
  const { statement, statementLoading } = useSelector((state) => state.invoice);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("Default");
  const [openExportConfirm, setOpenExportConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filters, setFilters] = useState({
    status: "Default",
    page: 1,
    rowsPerPage: 10,
  });
  const [editIndex, setEditIndex] = useState(null);
  const [editedCredit, setEditedCredit] = useState("");
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  useEffect(() => {
    if (!id) return;

    let statusParam;
    if (filters.status === "Default") {
      statusParam = undefined;
    } else if (filters.status === "All") {
      statusParam = "All";
    } else {
      statusParam = filters.status;
    }

    dispatch(
      getBuyerAllInvoices({
        id,
        page: filters.page,
        limit: filters.rowsPerPage,
        ...(statusParam && { status: statusParam }),
      })
    );
  }, [dispatch, id, filters]);

  // Process API data
  const processInvoices = () => {
    if (!buyerInvoices || !Array.isArray(buyerInvoices)) return [];
    return buyerInvoices.map((invoice) => ({
      id: invoice.invoiceNumber || `INV${invoice._id?.slice(-6) || "000000"}`,
      invoiceId: invoice?._id,
      date: formatDate(invoice.createdAt),
      seller: invoice.seller?.name || "Unknown Seller",
      sellerBankDetails: invoice.seller?.bankDetails || null,
      orderId: invoice.order?._id || "N/A",
      method: "Credit",
      amount: invoice.amount || 0,
      status: invoice.status || "Pending",
      dueDate: formatDate(invoice.dueDate),
      // New Interest Fields added here
      interestRate: invoice.interestRatePerYear || 0,
      interestStartDate: formatDate(invoice.interestAccrualStartDate),
      latestBalance: invoice.latestBalance || 0,
      orderItems: invoice.order?.items || [],
      originalData: invoice,
    }));
  };

  const processedPayments = processInvoices();
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionPaymentId, setActionPaymentId] = useState(null);

  const filteredPayments = (() => {
    if (statusFilter === "Default") {
      return processedPayments.filter(
        (p) => p.status === "Pending" || p.status === "Overdue"
      );
    }
    if (statusFilter === "All") {
      return processedPayments;
    }
    return processedPayments.filter((p) => p.status === statusFilter);
  })();

  const handleActionsMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setActionPaymentId(id);
  };
  const handleActionsMenuClose = () => {
    setAnchorEl(null);
    setActionPaymentId(null);
  };

  // Open Bank Statement
  const handleBankStatementOpen = (payment) => {
    setSelectedInvoiceForStatement(payment);
    setOpenBankStatement(true);
    handleActionsMenuClose();
  };

  const handleBankStatementClose = () => {
    setOpenBankStatement(false);
    setSelectedInvoiceForStatement(null);
  };

  const handleOpenOrderModal = (row) => {
    setSelectedOrder(row);
    setOpenOrderModal(true);
  };

  const handleCloseOrderModal = () => {
    setOpenOrderModal(false);
    setSelectedOrder(null);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");

    doc.setFontSize(16);
    doc.text("Invoices & Interest Report", 14, 15);

    const columns = [
      "#",
      "Order ID",
      "Invoice #",
      "Seller",
      "Amount",
      "Due Date",
      "Interest %",
      "Interest Start",
      "Latest Balance",
      "Status",
    ];

    const rows = filteredPayments.map((row, index) => [
      index + 1,
      row.orderId,
      row.id,
      row.seller,
      formatCurrency(row.amount),
      row.dueDate,
      `${row.interestRate}%`,
      row.interestStartDate,
      formatCurrency(row.latestBalance),
      row.status,
    ]);

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 25,
      styles: { fontSize: 9 },
      headStyles: {
        fillColor: [40, 0, 113],
      },
    });

    doc.save(`Invoices_Report_${Date.now()}.pdf`);
    setOpenExportConfirm(false);
  };

  const handleDownloadBankStatementPDF = () => {
    if (!selectedInvoiceForStatement) return;

    const doc = new jsPDF("p", "mm", "a4");

    const invoice = selectedInvoiceForStatement;
    const statement = invoice.originalData.bankStatement || [];

    // ===== HEADER =====
    doc.setFontSize(16);
    doc.setTextColor(40, 0, 113);
    doc.text("ACCOUNT STATEMENT", 14, 18);

    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text(`Invoice Ref : ${invoice.id}`, 14, 26);
    doc.text(`Seller       : ${invoice.seller}`, 14, 32);
    doc.text(`Interest    : ${invoice.interestRate}% p.a.`, 14, 38);
    doc.text(`Generated On : ${new Date().toLocaleString("en-IN")}`, 14, 44);

    // ===== TABLE DATA =====
    const tableBody = statement.map((txn) => [
      formatDate(txn.date),
      txn.description?.toUpperCase() || "-",
      txn.debit > 0 ? formatCurrency(txn.debit) : "",
      txn.credit > 0 ? formatCurrency(txn.credit) : "",
      formatCurrency(txn.balance),
    ]);

    autoTable(doc, {
      startY: 52,
      head: [["Txn Date", "Narration", "Debit", "Credit", "Balance"]],
      body: tableBody,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [238, 242, 248],
        textColor: 0,
        fontStyle: "bold",
      },
      columnStyles: {
        2: { halign: "right", textColor: [211, 47, 47] },
        3: { halign: "right", textColor: [46, 125, 50] },
        4: { halign: "right", fontStyle: "bold" },
      },
    });

    // const interestSummary = calculateInterest(invoice);

    // doc.setFontSize(11);
    // doc.text(
    //   `Interest Days     : ${interestSummary.days}`,
    //   14,
    //   doc.lastAutoTable.finalY + 15
    // );
    // doc.text(
    //   `Interest Amount  : ₹${formatCurrency(interestSummary.interest)}`,
    //   14,
    //   doc.lastAutoTable.finalY + 22
    // );
    // doc.text(
    //   `Total Payable    : ₹${formatCurrency(interestSummary.total)}`,
    //   14,
    //   doc.lastAutoTable.finalY + 29
    // );

    // FOOTER
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      "This is a system generated statement. No signature required.",
      14,
      finalY
    );

    doc.save(`Bank_Statement_${invoice.id}.pdf`);
  };

  const calculateInterest = (invoice) => {
    if (!invoice?.originalData?.interestAccrualStartDate) {
      return { days: 0, interest: 0, total: invoice.amount };
    }

    const rate = invoice.interestRate / 100;
    const principal = invoice.amount;

    const startDate = new Date(invoice.originalData.interestAccrualStartDate);
    const today = new Date();

    // 🔒 SAFETY: no negative days
    let days = Math.floor(
      (today.setHours(0, 0, 0, 0) - startDate.setHours(0, 0, 0, 0)) /
        (1000 * 60 * 60 * 24)
    );

    if (days < 0) days = 0;

    const interest = (principal * rate * days) / 365;

    return {
      days,
      interest: Math.round(interest * 100) / 100,
      total: Math.round((principal + interest) * 100) / 100,
    };
  };

  const calculateInterestFromStatement = (bankStatement = [], principal) => {
    const interestEntries = bankStatement.filter(
      (e) => e.description?.toLowerCase().includes("interest") && e.debit > 0
    );

    const totalInterest = interestEntries.reduce((sum, e) => sum + e.debit, 0);

    return {
      days: interestEntries.length,
      interest: Math.round(totalInterest * 100) / 100,
      total: Math.round((principal + totalInterest) * 100) / 100,
    };
  };

  const handleDownloadDateRangeStatement = () => {
    const invoice = selectedInvoiceForStatement;
    if (!invoice || !fromDate || !toDate) return;

    const doc = new jsPDF();
    const allTxns = invoice.originalData.bankStatement || [];

    const filtered = allTxns.filter((t) => {
      const d = new Date(t.date);
      return d >= new Date(fromDate) && d <= new Date(toDate);
    });

    autoTable(doc, {
      head: [["Date", "Narration", "Debit", "Credit", "Balance"]],
      body: filtered.map((t) => [
        formatDate(t.date),
        t.description,
        t.debit || "",
        t.credit || "",
        formatCurrency(t.balance),
      ]),
    });

    doc.save(`Statement_${fromDate}_to_${toDate}.pdf`);
  };

  useEffect(() => {
    if (openBankStatement && selectedInvoiceForStatement?.invoiceId) {
      dispatch(getInvoiceStatement(selectedInvoiceForStatement?.invoiceId));
    }
  }, [openBankStatement, selectedInvoiceForStatement, dispatch]);

  const handleEdit = (index, credit) => {
    setEditIndex(index);
    setEditedCredit(credit);
  };

  const handleCancel = () => {
    setEditIndex(null);
    setEditedCredit("");
  };

  const handleSave = (index, id) => {
    if (!editedCredit || Number(editedCredit) < 0) {
      toast.error("Please enter a valid credit amount");
      return;
    }

    dispatch(
      updateCredit({ bankStatementId: id, credit: Number(editedCredit) })
    )
      .unwrap()
      .then(() => {
        toast.success("Credit updated successfully");
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to update credit");
      });

    setEditIndex(null);
    setEditedCredit("");
  };
  // 🔹 Open Delete Dialog
  const handleDeleteClick = (index, id) => {
    setDeleteIndex(index);
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  // Confirm Delete
  const handleConfirmDelete = () => {
    if (!selectedInvoiceForStatement?.invoiceId || !deleteId) {
      toast.error("Invalid invoice or entry");
      return;
    }

    dispatch(
      deleteBankStatement({
        invoiceId: selectedInvoiceForStatement.invoiceId,
        bankStatementId: deleteId,
      })
    )
      .unwrap()
      .then(() => {
        toast.success("Entry deleted successfully");
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to delete entry");
      });

    setOpenDeleteDialog(false);
    setDeleteIndex(null);
    setDeleteId(null);
  };

  // 🔹 Cancel Delete
  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteIndex(null);
    setDeleteId(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography>Loading invoices...</Typography>
      </Box>
    );
  }

  return (
   <>
   <PageTitle 
   title="Pending Invoices & Interest Report - BizBridge"
   description="Track and manage all your pending invoices with interest calculations. View real-time credit statements, download bank statements, monitor interest accrual, and manage payments efficiently."
   keywords="pending invoices, interest report, credit management, bank statements, invoice tracking, payment due, interest calculation, B2B payments, credit terms, overdue invoices, payment tracking."    
   url="/buyer-dashboard/pending-invoices"
   />
   <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: "#f8fafc",
        py: { xs: 1, sm: 2 },
      }}
    >
       {/* Breadcrumb */}
            <Breadcrumbs
              aria-label="breadcrumb"
              sx={{
                width: "fit-content",
                backgroundColor: "#fff",
                ml: { xs: 1, sm: 2.9 },
                padding: "13px",
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            >
              <MUILink
                component={Link}
                to="/buyer-dashboard/my-orders"
                sx={{ color: "inherit", textDecoration: "none" }}
              >
                My Orders
              </MUILink>
              <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
                Pending Invoices
              </Typography>
            </Breadcrumbs>
      
            <Divider sx={{ my: 2 }} />

      <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
         <Grid container spacing={2} alignItems="center" mb={3}>
                  <Grid item xs={12} md={6}>
                    <Typography fontWeight={700} fontSize={22}>
                      Pending Invoices & Interest Report
                    </Typography>
                  </Grid>
        
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          label="Status"
                          onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                          }}
                        >
                          <MenuItem value="Default">Default</MenuItem>
                          <MenuItem value="Paid">Paid</MenuItem>
                          <MenuItem value="Pending">Pending</MenuItem>
                          <MenuItem value="Overdue">Overdue</MenuItem>
                        </Select>
                      </FormControl>
        
                      <Button
                        startIcon={<DownloadIcon />}
                        variant="outlined"
                        sx={{ borderRadius: 2, bgcolor: "#fff" }}
                        onClick={() => setOpenExportConfirm(true)}
                      >
                        Export
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

        <Paper elevation={0} sx={{ borderRadius: 2, mb: 3, overflowX: "auto" }}>
          {!isMobile ? (
            <TableContainer>
              <Table sx={{ overflowX: "auto" }}>
                <TableHead>
                  <TableRow sx={{ background: "#f6f8fa" }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    {/* <TableCell sx={{ fontWeight: 700 }}>OrderId</TableCell> */}
                    <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Seller</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount (₹)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#d32f2f" }}>
                      Interest Rate
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#d32f2f" }}>
                      Latest Balance
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>QR Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>New Entry</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPayments.map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600 }}
                        onClick={() => handleOpenOrderModal(row)}
                      >
                        <Button
                          variant="text"
                          sx={{ fontWeight: 700, textTransform: "none", p: 0 }}
                        >
                          {row.id}
                        </Button>

                        <Typography
                          variant="caption"
                          display="block"
                          color="textSecondary"
                          sx={{
                            fontWeight: 700,
                            textTransform: "none",
                            p: 0,
                            cursor: "pointer",
                          }}
                        >
                          {row.date}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.seller}</TableCell>
                      <TableCell>₹{formatCurrency(row.amount)}</TableCell>
                      <TableCell>{row.dueDate}</TableCell>
                      {/* Interest Data Display */}
                      <TableCell>
                        <Chip
                          label={`${row.interestRate}% p.a.`}
                          size="small"
                          variant="outlined"
                          color="error"
                          sx={{
                            borderColor: "#ffcdd2",
                            color: "#c62828",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>{formatCurrency(row.latestBalance)}</TableCell>

                      <TableCell>
                        <Chip
                          label={row.status}
                          color={statusColor(row.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>

                      <TableCell>
                        <GetQRCodeModal
                          seller={row.sellerBankDetails}
                          name={row.seller}
                          trigger={
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{ borderRadius: 2 }}
                            >
                              Pay
                            </Button>
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <AddEntryModal
                          invoiceId={row.invoiceId}
                          seller={row.seller}
                          id={row.id}
                          dueDate={row.date}
                          trigger={
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{ borderRadius: 2 }}
                            >
                              New Entry
                            </Button>
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleActionsMenuOpen(e, row.id)}
                          size="small"
                        >
                          <MoreHorizIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={actionPaymentId === row.id && Boolean(anchorEl)}
                          onClose={handleActionsMenuClose}
                        >
                          <MenuItem
                            onClick={() => handleBankStatementOpen(row)}
                          >
                            <ListItemIcon>
                              <AccountBalanceIcon fontSize="small" />
                            </ListItemIcon>{" "}
                            Statement
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={buyerPagination?.totalInvoices || 0}
                page={(buyerPagination?.currentPage || 1) - 1}
                onPageChange={(event, newPage) => {
                  setPage(newPage + 1);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            </TableContainer>
          ) : (
            // Mobile View
            <Box>
              {filteredPayments.map((row, index) => (
                <Card
                  key={row.id}
                  variant="outlined"
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography fontWeight={700}>{index + 1}</Typography>
                      <Typography fontWeight={700}>
                        {" "}
                        Invoice# {row.id}
                      </Typography>
                      <Chip
                        label={row.status}
                        color={statusColor(row.status)}
                        size="small"
                      />
                    </Stack>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Buyer: {row.buyer}
                    </Typography>
                    <Typography variant="body2">
                      Amount: ₹{formatCurrency(row.amount)}
                    </Typography>

                    {/* Interest details in mobile */}
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        bgcolor: "#fff5f5",
                        borderRadius: 1,
                        border: "1px dashed #ffcdd2",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="error"
                        fontWeight={600}
                        display="block"
                      >
                        Interest: {row.interestRate}% per month
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Accrual Starts: {row.interestStartDate}
                      </Typography>
                    </Box>

                    <Stack direction="row" justifyContent="flex-end" mt={1}>
                      <Button
                        size="small"
                        startIcon={<AccountBalanceIcon />}
                        onClick={() => handleBankStatementOpen(row)}
                      >
                        Statement
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              <TablePagination
                component="div"
                count={buyerPagination?.totalInvoices || 0}
                page={(buyerPagination?.currentPage || 1) - 1}
                onPageChange={(event, newPage) => {
                  setPage(newPage + 1);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            </Box>
          )}
        </Paper>

        {/* ORDER MODAL FOR ORDER DETAILS */}
        <Dialog
          open={openOrderModal}
          onClose={handleCloseOrderModal}
          maxWidth="md"
          fullWidth
        >
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={2}>
              Order Details – Invoice #{selectedOrder?.id}
            </Typography>

            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Price (₹)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Total (₹)
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedOrder?.orderItems?.map((item, index) => (
                    <TableRow key={item._id || index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>

                      <TableCell>
                        <img
                          src={item.image}
                          alt={item.name}
                          style={{
                            width: 50,
                            height: 50,
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #ddd",
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">{item.quantity}</TableCell>

                      <TableCell align="right">
                        ₹{formatCurrency(item.price)}
                      </TableCell>

                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ₹{formatCurrency(item.finalPrice)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: "right" }}>
              <Typography fontWeight={700}>
                Order Total: ₹{formatCurrency(selectedOrder?.amount)}
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseOrderModal} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* BANK STATEMENT DIALOG */}
        <Dialog
          open={openBankStatement}
          onClose={handleBankStatementClose}
          fullWidth
          maxWidth="lg"
        >
          <Box sx={{ bgcolor: "#fff", p: 0 }} ref={printRef}>
            <Box
              sx={{
                p: 3,
                borderBottom: "4px solid #0b4064",
                background: "#fcfcfc",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    color: "#0b4064",
                    fontWeight: 700,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <AccountBalanceIcon fontSize="large" /> ACCOUNT STATEMENT
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Generated on: {new Date().toLocaleString("en-IN")}
                </Typography>
              </Box>

              {selectedInvoiceForStatement && (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    INVOICE REF: {selectedInvoiceForStatement.id}
                  </Typography>
                  <Typography variant="body2">
                    Seller: {selectedInvoiceForStatement.seller}
                  </Typography>
                  <Typography variant="body2">
                    Interest Rate: {selectedInvoiceForStatement.interestRate}%
                    p.a.
                  </Typography>
                </Box>
              )}
            </Box>

            {selectedInvoiceForStatement &&
              (() => {
                const bankStatement =
                  selectedInvoiceForStatement.originalData.bankStatement;

                const summary = calculateInterestFromStatement(
                  bankStatement,
                  selectedInvoiceForStatement.amount
                );

                return (
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #ffd6d6",
                      borderRadius: 2,
                      background: "#fff5f5",
                    }}
                  >
                    <Stack direction="row" spacing={4} flexWrap="wrap">
                      <Typography fontWeight={600}>
                        Due Date : {selectedInvoiceForStatement.dueDate}
                      </Typography>

                      <Typography fontWeight={600}>
                        Days Passed: {summary.days}
                      </Typography>

                      <Typography fontWeight={600}>
                        Interest Till Today: ₹{formatCurrency(summary.interest)}
                      </Typography>

                      <Typography fontWeight={700} color="error">
                        Total Payable: ₹{formatCurrency(summary.total)}
                      </Typography>
                    </Stack>
                  </Paper>
                );
              })()}

            <DialogContent sx={{ p: { xs: 1, md: 4 }, bgcolor: "#fff" }}>
              {/* DATE RANGE TOOLBAR */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                  background: "#fafafa",
                }}
              >
                <TextField
                  type="date"
                  size="small"
                  label="From Date"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />

                <TextField
                  type="date"
                  size="small"
                  label="To Date"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />

                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  disabled={!fromDate || !toDate}
                  onClick={handleDownloadDateRangeStatement}
                  sx={{ ml: "auto" }}
                >
                  Date Range PDF
                </Button>
              </Box>
              {selectedInvoiceForStatement?.originalData?.bankStatement
                ?.length > 0 ? (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: "1px solid #ccc" }}
                >
                  <Table size="small" sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#eef2f8", height: "40px" }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: "#333",
                            borderRight: "1px solid #ddd",
                          }}
                        >
                          Txn Date
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: "#333",
                            borderRight: "1px solid #ddd",
                          }}
                        >
                          Narration / Description
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            color: "#333",
                            borderRight: "1px solid #ddd",
                          }}
                        >
                          Debit (₹)
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            color: "#333",
                            borderRight: "1px solid #ddd",
                          }}
                        >
                          Credit (₹)
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, color: "#333" }}
                        >
                          Balance (₹)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoiceForStatement.originalData.bankStatement.map(
                        (entry, index) => (
                          <TableRow
                            key={entry._id || index}
                            sx={{
                              "&:nth-of-type(even)": { bgcolor: "#fbfbfb" },
                              "&:hover": { bgcolor: "#f5f5f5" },
                            }}
                          >
                            <TableCell
                              sx={{
                                borderRight: "1px solid #eee",
                                fontSize: "13px",
                              }}
                            >
                              {formatDate(entry.date)}
                            </TableCell>
                            <TableCell
                              sx={{
                                borderRight: "1px solid #eee",
                                fontSize: "13px",
                                textTransform: "uppercase",
                              }}
                            >
                              {entry.description}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderRight: "1px solid #eee",
                                fontSize: "13px",
                                color: "#d32f2f",
                                fontWeight: entry.debit > 0 ? 600 : 400,
                              }}
                            >
                              {entry.debit > 0
                                ? formatCurrency(entry.debit)
                                : ""}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderRight: "1px solid #eee",
                                fontSize: "13px",
                                color: "#2e7d32",
                                fontWeight: entry.credit > 0 ? 600 : 400,
                              }}
                            >
                              {entry.credit > 0
                                ? formatCurrency(entry.credit)
                                : ""}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontSize: "13px", fontWeight: 700 }}
                            >
                              {formatCurrency(entry.balance)}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 5,
                    color: "#888",
                    border: "1px dashed #ccc",
                    borderRadius: 2,
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                  <Typography>
                    No transactions recorded for this invoice yet.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <Divider />
            <>
              {statement?.length > 0 && (
                <Box sx={{ px: 2, pt: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Payment / Credit Entries
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    These credit entries are awaiting seller approval and will
                    be added to the bank statement once approved.
                  </Typography>
                </Box>
              )}

              {statement?.length > 0 &&
                statement.map((item, index) => {
                  if (!item?.credit || item.credit <= 0) return null;

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      {/* CREDIT */}
                      {editIndex === index ? (
                        <TextField
                          size="small"
                          label="Edit Credit Amount"
                          type="number"
                          value={editedCredit}
                          onChange={(e) => setEditedCredit(e.target.value)}
                        />
                      ) : (
                        <Typography variant="body2">
                          {" "}
                          Credit Amount(₹): {item.credit}
                        </Typography>
                      )}

                      {/* ACTION BUTTONS */}
                      <Box>
                        {editIndex === index ? (
                          <>
                            <IconButton
                              onClick={() => handleSave(index, item._id)}
                              color="success"
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton onClick={handleCancel} color="error">
                              <CloseIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              onClick={() => handleEdit(index, item.credit)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              // onClick={() => handleDelete(index, item._id)}
                              onClick={() => handleDeleteClick(index, item._id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
            </>

            <DialogActions sx={{ p: 2, bgcolor: "#f9f9f9" }}>
              <Button
                onClick={handleBankStatementClose}
                variant="outlined"
                color="primary"
              >
                Close Statement
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadBankStatementPDF}
              >
                Download PDF
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      </Box>

      <Dialog
        open={openExportConfirm}
        onClose={() => setOpenExportConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <DownloadIcon sx={{ fontSize: 48, color: "#0b4064", mb: 1 }} />

          <Typography variant="h6" fontWeight={700} gutterBottom>
            Download PDF?
          </Typography>

          <Typography variant="body2" color="textSecondary">
            Are you sure you want to export the current invoice list as a PDF
            file?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setOpenExportConfirm(false)}
            disabled={exporting}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleExportPDF}
            disabled={exporting}
          >
            {exporting ? "Downloading..." : "Yes, Download"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this entry? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
   </>
  );
}
